import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  addDays,
  APPOINTMENT_MAX_DAYS,
  daysBetween,
  PLANNING_KIND_LABELS,
  planningDay,
  todayIso,
  weekdayIndex,
  type AppointmentInput,
  type AssetDetail,
  type PlanningAppointmentDto,
  type PlanningLineDto,
} from "@klantpunt/shared";
import { Segmented } from "../../app/Choice";
import { Meta } from "../../app/Meta";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { assetKey } from "../assets/AssetPanel";
import { MailDialog, type MailPreset, type MailTarget } from "../directory/Mail";
import { NO_PARTY, PartyPicker, type Party } from "../directory/PartyPicker";
import { BookDialog, needsBooking } from "../materials/TaskMaterials";
import { RecordDialog } from "../obligations/RecordDialog";
import { useAssetObligations, useObligationsCache } from "../obligations/useObligations";
import { useTaskRefresh, useTasks } from "../tasks/useTasks";
import { whenText } from "./PlanningGrid";
import { appointmentText, lineHref, type PlanRow } from "./planningView";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");

/** Afspraak opslaan (nieuw of verplaatst) en de planning verversen. */
export function useAppointments() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["planning"] });
  /** Opslaan zonder de planning te verversen (voor meerdere regels achter elkaar). */
  const put = (l: PlanningLineDto, body: Omit<AppointmentInput, "source" | "refId">) =>
    api<PlanningAppointmentDto>(`/planning/appointments/${l.appointment?.id ?? crypto.randomUUID()}`, {
      method: "PUT",
      body: { source: l.source, refId: l.refId, ...body },
    });
  return {
    refresh,
    put,
    save: async (l: PlanningLineDto, body: Omit<AppointmentInput, "source" | "refId">) => {
      const a = await put(l, body);
      await refresh();
      return a;
    },
    remove: async (l: PlanningLineDto) => {
      if (l.appointment) await api(`/planning/appointments/${l.appointment.id}`, { method: "DELETE" });
      await refresh();
    },
  };
}

/** Wat de afspraak bewaart als uitvoerder (adresboek of vrije naam). */
export const performerBody = (p: Party) => ({
  performerCompanyId: p.companyId,
  performerPersonId: p.personId,
  performer: p.companyId ? null : p.text.trim() || null,
});

/** De afspraak blijft zoals hij is, alleen op een andere dag (slepen, verplaatsen). */
export const moveBody = (a: PlanningAppointmentDto, startOn: string) => ({
  startOn,
  days: a.days,
  confirmed: a.confirmed,
  note: a.note,
  ...performerBody({
    ...NO_PARTY,
    companyId: a.performer?.companyId ?? null,
    personId: a.performer?.personId ?? null,
    text: a.performer?.companyId ? "" : (a.performer?.name ?? ""),
  }),
});

/** Hulpteksten bij de gekozen datum: verleden, weekend, en hoe het zich verhoudt tot de vervaldatum. */
function dateHints(l: PlanningLineDto, start: string, days: number, today: string) {
  const out: { text: string; warn: boolean }[] = [];
  if (!start) return out;
  const end = addDays(start, days - 1);
  if (start < today) out.push({ text: "Deze datum ligt in het verleden.", warn: true });
  if (Array.from({ length: Math.min(days, 7) }, (_, i) => weekdayIndex(addDays(start, i))).some((d) => d >= 5))
    out.push({ text: "Valt (deels) in het weekend.", warn: false });
  if (l.dueOn) {
    const late = daysBetween(l.dueOn, end);
    if (late > 0)
      out.push({
        text: `${late} ${late === 1 ? "dag" : "dagen"} na de vervaldatum (${planningDay(l.dueOn)}).${
          l.legal ? " Dit is wettelijk verplicht: plan het liefst op of vóór de vervaldatum." : ""
        }`,
        warn: true,
      });
    else
      out.push({
        text: `Klaar ${-late === 0 ? "op" : `${-late} ${late === -1 ? "dag" : "dagen"} vóór`} de vervaldatum (${planningDay(l.dueOn)}).`,
        warn: false,
      });
  }
  return out;
}

const norm = (s: string | null | undefined) => (s ?? "").trim().toLocaleLowerCase("nl");

/**
 * Andere regels in hetzelfde gebouw bij dezelfde uitvoerder die je in hetzelfde bezoek kunt meenemen: open, binnen
 * 120 dagen aan de beurt (of verlopen), en nog niet op dezelfde dag gepland.
 */
export function bundleCandidates(rows: PlanRow[], row: PlanRow, who: Party, start: string, today: string) {
  if (!who.companyId && !who.text.trim()) return [];
  const horizon = addDays(today, 120);
  return rows.filter(({ l, status }) => {
    if (l.id === row.l.id || l.building.id !== row.l.building.id || !l.canPlan || status === "klaar") return false;
    if (!l.dueOn || l.dueOn > horizon || l.appointment?.startOn === start) return false;
    const p = l.appointment?.performer ?? l.performer;
    return who.companyId ? p?.companyId === who.companyId : !p?.companyId && norm(p?.name) === norm(who.text);
  });
}

/**
 * Regel uit de planning openen (planning stap 3): inplannen of verplaatsen met datum, duur, uitvoerder en
 * voorlopig of bevestigd; de uitvoering vastleggen of de taak afronden; of de bron openen.
 */
export function PlanningLineDialog({ row, rows, onClose }: { row: PlanRow; rows: PlanRow[]; onClose: () => void }) {
  const { l } = row;
  const a = l.appointment;
  const today = todayIso();
  const toast = useToast();
  const { data: me } = useMe();
  const appointments = useAppointments();
  const [start, setStart] = useState(a?.startOn ?? (l.dueOn && l.dueOn > today ? l.dueOn : today));
  const [days, setDays] = useState(a?.days ?? 1);
  const [confirmed, setConfirmed] = useState(a?.confirmed ?? false);
  const [note, setNote] = useState(a?.note ?? "");
  const saved = a?.performer ?? l.performer;
  const [performer, setPerformer] = useState<Party>(() => ({
    ...NO_PARTY,
    companyId: saved?.companyId ?? null,
    personId: saved?.personId ?? null,
    text: saved?.companyId ? "" : (saved?.name ?? ""),
  }));
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mailing, setMailing] = useState(false);
  const hints = dateHints(l, start, days, today);
  const [bundle, setBundle] = useState<string[]>([]);
  const candidates = bundleCandidates(rows, row, performer, start, today);
  const bundled = candidates.filter((r) => bundle.includes(r.l.id));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!start) return;
    setBusy(true);
    try {
      const body = { startOn: start, days, confirmed, note, ...performerBody(performer) };
      // Eerst de regels die mee gaan in hetzelfde bezoek (notitie hoort alleen bij deze regel).
      for (const r of bundled) await appointments.put(r.l, { ...body, note: r.l.appointment?.note ?? null });
      const saved = await appointments.save(l, body);
      toast(
        `${a ? "Afspraak bijgewerkt" : "Ingepland"}: ${appointmentText(saved)}${
          bundled.length ? `, ${bundled.length + 1} regels in één bezoek` : ""
        }`,
      );
      onClose();
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await appointments.remove(l);
      toast("Afspraak gewist");
      onClose();
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  const meta = [
    `${l.building.code} ${l.building.name}`,
    l.asset && `${l.asset.code} ${l.asset.name}`,
    l.code,
    PLANNING_KIND_LABELS[l.kind],
  ];
  const mayRecord =
    l.source === "obligation"
      ? can(me, "asset.manage") && hasModule(me, "assets", "edit")
      : l.source === "task" && can(me, "task.manage");

  // Mailen kan met de opgeslagen afspraak; na een wijziging eerst opslaan (anders klopt de uitnodiging niet).
  const mailTarget: MailTarget | null = a?.performer?.companyId
    ? { companyId: a.performer.companyId }
    : a?.performer?.personId
      ? { personId: a.performer.personId }
      : null;
  const mayMail = !!mailTarget && hasModule(me, "directory", "edit");
  const dirty =
    !!a &&
    (start !== a.startOn ||
      days !== a.days ||
      confirmed !== a.confirmed ||
      note !== (a.note ?? "") ||
      performer.companyId !== (a.performer?.companyId ?? null) ||
      performer.personId !== (a.performer?.personId ?? null));

  // Eén venster tegelijk: vastleggen, afronden of mailen vervangt dit venster; Annuleren komt hier terug.
  if (mailing && a && mailTarget)
    return (
      <MailDialog
        target={mailTarget}
        links={[]}
        preset={appointmentMail(l, a, rows, me?.user.name ?? null)}
        onClose={() => setMailing(false)}
      />
    );
  if (recording && l.source === "obligation")
    return <RecordFromPlanning l={l} onClose={() => setRecording(false)} onDone={onClose} />;
  if (recording && l.source === "task")
    return <FinishTask l={l} onClose={() => setRecording(false)} onDone={onClose} />;

  return (
    <Modal
      title={l.title}
      onClose={onClose}
      busy={busy}
      footer={
        l.canPlan ? (
          <>
            {a && (
              <button type="button" className="btn danger" disabled={busy} onClick={() => void clear()}>
                Afspraak wissen
              </button>
            )}
            <button type="button" className="btn ghost" onClick={onClose}>
              Annuleren
            </button>
            <button type="submit" form="planform" className="btn primary" disabled={busy || !start}>
              {a ? "Opslaan" : "Inplannen"}
            </button>
          </>
        ) : undefined
      }
    >
      <div className="stack">
        <div>
          <Meta className="small muted" items={meta} />
          <p className={`small kp-when kp-when-${row.status}`}>{whenText(row)}</p>
        </div>
        {l.canPlan ? (
          <form id="planform" className="stack" onSubmit={submit}>
            <div className="formgrid">
              <label className="field">
                <span className="label">Plandatum *</span>
                <input
                  type="date"
                  required
                  value={start}
                  aria-describedby="plan-hints"
                  onChange={(e) => setStart(e.target.value)}
                />
              </label>
              <label className="field">
                <span className="label">Aantal dagen</span>
                <input
                  type="number"
                  min={1}
                  max={APPOINTMENT_MAX_DAYS}
                  value={days}
                  onChange={(e) =>
                    setDays(Math.min(APPOINTMENT_MAX_DAYS, Math.max(1, Math.round(Number(e.target.value) || 1))))
                  }
                />
              </label>
            </div>
            <div className="row">
              <button type="button" className="btn small" disabled={!start} onClick={() => setStart(addDays(start, 1))}>
                Dag later
              </button>
              <button type="button" className="btn small" disabled={!start} onClick={() => setStart(addDays(start, 7))}>
                Week later
              </button>
            </div>
            <ul id="plan-hints" className="kp-hints small" aria-live="polite">
              {hints.map((h) => (
                <li key={h.text} className={h.warn ? "kp-hint-warn" : "muted"}>
                  {h.text}
                </li>
              ))}
            </ul>
            <PartyPicker
              id="plan-performer"
              value={performer}
              onChange={setPerformer}
              savedName={saved?.name}
              buildingId={l.building.id}
            />
            {candidates.length > 0 && (
              <fieldset className="field plain">
                <legend className="label">Meenemen in hetzelfde bezoek</legend>
                <span className="hint">
                  Andere regels in dit gebouw bij dezelfde uitvoerder krijgen dezelfde afspraak.
                </span>
                <div className="checklist">
                  {candidates.map((r) => (
                    <label key={r.l.id}>
                      <input
                        type="checkbox"
                        checked={bundle.includes(r.l.id)}
                        onChange={(e) =>
                          setBundle(e.target.checked ? [...bundle, r.l.id] : bundle.filter((x) => x !== r.l.id))
                        }
                      />
                      <span>
                        {r.l.title}
                        <Meta
                          className="small muted"
                          items={[r.l.asset && `${r.l.asset.code} ${r.l.asset.name}`, r.l.code, whenText(r)]}
                        />
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            <div className="field">
              <span className="label" aria-hidden="true">
                Afspraak
              </span>
              <Segmented<"voorlopig" | "bevestigd">
                label="Afspraak"
                value={confirmed ? "bevestigd" : "voorlopig"}
                onChange={(v) => setConfirmed(v === "bevestigd")}
                items={[
                  { value: "voorlopig", label: "Voorlopig" },
                  { value: "bevestigd", label: "Bevestigd" },
                ]}
              />
              <span className="hint">Bevestigd als de uitvoerder de datum heeft toegezegd.</span>
            </div>
            <label className="field">
              <span className="label">Notitie</span>
              <textarea rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
          </form>
        ) : (
          a && <p className="small">Gepland: {appointmentText(a)}</p>
        )}
        <div className="row kp-dlgactions">
          {a && mayMail && (
            <button
              type="button"
              className="btn small"
              disabled={dirty}
              aria-describedby={dirty ? "plan-mail-hint" : undefined}
              onClick={() => setMailing(true)}
            >
              Mail naar uitvoerder
            </button>
          )}
          {mayRecord && (
            <button type="button" className="btn small" onClick={() => setRecording(true)}>
              {l.source === "task" ? "Taak afronden" : "Uitvoering vastleggen"}
            </button>
          )}
          <Link className="btn small ghost" to={lineHref(l)}>
            {l.source === "task"
              ? "Taak openen"
              : l.source === "meter"
                ? "Meterronde openen"
                : l.asset
                  ? "Asset openen"
                  : "Gebouw openen"}
          </Link>
        </div>
        {a && mayMail && dirty && (
          <p id="plan-mail-hint" className="hint">
            Sla de afspraak eerst op; daarna kun je hem mailen.
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Voorstel voor de mail aan de uitvoerder: datum, gebouw en de andere regels in hetzelfde bezoek (zelfde gebouw,
 * dag en uitvoerder). De notitie staat erin zodat de planner zelf kiest of die mee gaat; de uitnodiging zelf
 * bevat hem niet.
 */
export function appointmentMail(
  l: PlanningLineDto,
  a: PlanningAppointmentDto,
  rows: PlanRow[],
  senderName: string | null,
): MailPreset {
  const p = a.performer;
  const samePerformer = (o: PlanningAppointmentDto["performer"]) =>
    !!o && (p?.companyId ? o.companyId === p.companyId : !!p?.personId && o.personId === p.personId);
  const others = rows
    .map((r) => r.l)
    .filter(
      (x) =>
        x.id !== l.id &&
        x.building.id === l.building.id &&
        x.appointment?.startOn === a.startOn &&
        samePerformer(x.appointment.performer),
    );
  const what = (x: PlanningLineDto) =>
    [x.title, x.asset && `(${x.asset.code} ${x.asset.name})`].filter(Boolean).join(" ");
  const building = `${l.building.code} ${l.building.name}`;
  const lines = [
    // Persoon bij een bedrijf heet "Naam (Bedrijf)"; groet alleen met de naam.
    `Beste${p?.personId && p.name ? ` ${p.name.replace(/\s*\([^)]*\)$/, "")}` : ""},`,
    "",
    `Hierbij de afspraak voor ${what(l)} in ${building}.`,
    "",
    `Datum: ${appointmentText(a)}`,
    ...(others.length ? ["", "In hetzelfde bezoek:", ...others.map((x) => `- ${what(x)}`)] : []),
    ...(a.note ? ["", a.note] : []),
    "",
    "De afspraak zit als agenda-uitnodiging in de bijlage.",
    "",
    "Met vriendelijke groet,",
    ...(senderName ? [senderName] : []),
  ];
  return {
    subject: `Afspraak ${planningDay(a.startOn)}: ${l.title} (${l.building.code})`,
    body: lines.join("\n"),
    buildingId: l.building.id,
    to: p?.companyId ? p.personId : null,
    appointmentId: a.id,
  };
}

/** Uitvoering vastleggen met hetzelfde venster als bij de asset; daarna is de afspraak gedaan. */
function RecordFromPlanning({ l, onClose, onDone }: { l: PlanningLineDto; onClose: () => void; onDone: () => void }) {
  // Zonder asset: onderhoud of keuring bij het gebouw zelf.
  const assetId = l.asset?.id ?? null;
  const asset = useQuery({
    queryKey: assetKey(assetId ?? ""),
    queryFn: () => api<AssetDetail>(`/assets/${assetId}`),
    enabled: !!assetId,
  });
  const obligations = useAssetObligations(l.building.id, assetId);
  const set = useObligationsCache(l.building.id, assetId);
  const { refresh } = useAppointments();
  const o = obligations.data?.obligations.find((x) => x.id === l.refId);
  if ((assetId && !asset.data) || !o) return null;
  return (
    <RecordDialog
      asset={asset.data ?? null}
      buildingId={l.building.id}
      obligation={o}
      onClose={onClose}
      onSaved={(r) => {
        if (r) set(r);
        void refresh();
        onDone();
      }}
    />
  );
}

/** Taak op Klaar zetten; met materialen eerst afboeken (zoals op het takenbord). */
function FinishTask({ l, onClose, onDone }: { l: PlanningLineDto; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const { data: me } = useMe();
  const tasks = useTasks(l.building.id);
  const refreshTasks = useTaskRefresh(l.building.id);
  const { refresh } = useAppointments();
  const task = tasks.data?.find((t) => t.id === l.refId);
  const finish = async () => {
    await api(`/tasks/${l.refId}`, { method: "PATCH", body: { status: "klaar" } });
  };
  const book = Boolean(task && hasModule(me, "materials") && needsBooking(task));
  const [busy, setBusy] = useState(false);
  if (!task) return null;
  if (book)
    return (
      <BookDialog
        task={task}
        finish={finish}
        onClose={onClose}
        onDone={async () => {
          await Promise.all([refreshTasks(), refresh()]);
          onDone();
        }}
      />
    );
  return (
    <Modal
      title="Taak afronden"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await finish();
                await Promise.all([refreshTasks(), refresh()]);
                toast(`Taak ${task.code} is klaar`);
                onDone();
              } catch (err) {
                toast(errMsg(err));
                setBusy(false);
              }
            }}
          >
            Klaar
          </button>
        </>
      }
    >
      <p>
        <strong>{task.code}</strong> {task.title} op Klaar zetten? De taak verdwijnt dan uit de planning.
      </p>
    </Modal>
  );
}
